import { useState, type FormEvent } from "react";
import { api, ApiError } from "../api/client";

// Self-service password change for any logged-in user (spec §6.4, §7.2
// /auth/change-password).

export default function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (next.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      setError("New password and confirmation do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.changePassword(current, next);
      setDone(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not change password.");
    } finally {
      setBusy(false);
    }
  }

  const inputCls =
    "w-full rounded bg-slate-900 px-3 py-2 text-sm text-slate-100 outline-none ring-1 ring-slate-700 focus:ring-sky-500";

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Change password"
    >
      <div
        className="w-full max-w-sm overflow-hidden rounded-lg border border-slate-700 bg-slate-900 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-700 bg-slate-800 px-4 py-3">
          <h2 className="font-semibold text-slate-100">Change password</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded bg-slate-700 px-3 py-1.5 text-sm font-medium text-slate-100 hover:bg-slate-600"
          >
            Close
          </button>
        </div>
        {done ? (
          <div className="p-4 text-sm text-emerald-300">
            Password changed. Use your new password next time you sign in.
            <div className="mt-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-500"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3 p-4">
            {error && (
              <div className="rounded bg-red-950/50 px-3 py-2 text-sm text-red-300">{error}</div>
            )}
            <label className="block text-sm text-slate-300">
              Current password
              <input
                className={inputCls}
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                autoFocus
              />
            </label>
            <label className="block text-sm text-slate-300">
              New password (min 8)
              <input
                className={inputCls}
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
              />
            </label>
            <label className="block text-sm text-slate-300">
              Confirm new password
              <input
                className={inputCls}
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
            </label>
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
            >
              {busy ? "Changing…" : "Change password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
